import { useEditor, EditorContent } from '@tiptap/react';
import type { Editor as TiptapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, Quote, Maximize2, Minimize2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { sanitizeLetterHtml } from '@/utils/htmlUtils';

const MenuBar = ({ editor, isFullscreen, onToggleFullscreen }: { editor: TiptapEditor | null; isFullscreen: boolean; onToggleFullscreen: () => void }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="border-b border-gray-200 p-4 flex gap-2 items-center bg-white/80 backdrop-blur-sm sticky top-0">
      <div className="flex gap-2">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors ${
            editor.isActive('bold') ? 'bg-blue-50 text-blue-600' : ''
          }`}
        >
          <Bold className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors ${
            editor.isActive('italic') ? 'bg-blue-50 text-blue-600' : ''
          }`}
        >
          <Italic className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors ${
            editor.isActive('bulletList') ? 'bg-blue-50 text-blue-600' : ''
          }`}
        >
          <List className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors ${
            editor.isActive('blockquote') ? 'bg-blue-50 text-blue-600' : ''
          }`}
        >
          <Quote className="w-5 h-5" />
        </button>
      </div>
      <div className="ml-auto">
        <button
          onClick={onToggleFullscreen}
          className="p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors"
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? (
            <Minimize2 className="w-5 h-5" />
          ) : (
            <Maximize2 className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
};

interface EditorProps {
  onContentChange: (content: string) => void;
  initialContent?: string;
  hasError?: boolean;
}

export default function Editor({ onContentChange, initialContent, hasError }: EditorProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const safeInitialContent = initialContent ? sanitizeLetterHtml(initialContent) : undefined;

  const editor = useEditor({
    extensions: [StarterKit],
    content: safeInitialContent || 'Querido Eu do Futuro, ',
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none min-h-[300px] px-8 py-4',
      },
    },
    onUpdate: ({ editor }) => {
      onContentChange(sanitizeLetterHtml(editor.getHTML()));
    },
  });

  useEffect(() => {
    if (editor && safeInitialContent && editor.getHTML() !== safeInitialContent) {
      editor.commands.setContent(safeInitialContent);
    }
  }, [safeInitialContent, editor]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div
      className={`bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden transition-all duration-300 border ${
        hasError ? 'border-red-500' : 'border-gray-100'
      } ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''}`}
    >
      <MenuBar 
        editor={editor} 
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
      />
      <div className={`${isFullscreen ? 'h-[calc(100vh-64px)]' : ''}`}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}